import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for skeleton message bubbles
const SkeletonMessageBubble = styled(Box)(({ theme, isSender }) => ({
  position: 'relative',
  padding: '12px 16px',
  borderRadius: isSender 
    ? '18px 18px 4px 18px' 
    : '18px 18px 18px 4px',
  maxWidth: '70%',
  minWidth: '120px',
  backgroundColor: isSender ? '#008069' : '#ffffff',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  marginBottom: '8px',
}));

const SkeletonMessageContainer = styled(Box)(({ theme, isSender }) => ({
  display: 'flex',
  justifyContent: isSender ? 'flex-end' : 'flex-start',
  alignItems: 'flex-end',
  marginBottom: '16px',
  gap: '8px',
  padding: '0 16px',
}));

const SkeletonAvatar = styled(Skeleton)(({ theme, isSender }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.1)' : '#f0f2f5',
}));

const SkeletonText = styled(Skeleton)(({ theme, isSender }) => ({
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.2)' : '#f0f2f5',
  borderRadius: '4px',
}));

const SkeletonTimestamp = styled(Skeleton)(({ theme, isSender }) => ({
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.1)' : '#f0f2f5',
  borderRadius: '2px',
  marginTop: '4px',
}));

function MessageSkeleton({ isSender = false, variant = 'text' }) {
  const getRandomWidth = () => {
    const widths = [60, 70, 80, 90, 100];
    return widths[Math.floor(Math.random() * widths.length)];
  };

  const getRandomHeight = () => {
    const heights = [16, 20, 24, 28];
    return heights[Math.floor(Math.random() * heights.length)];
  };

  return (
    <SkeletonMessageContainer isSender={isSender}>
      {!isSender && (
        <SkeletonAvatar 
          isSender={isSender}
          variant="circular"
          animation="wave"
        />
      )}
      <SkeletonMessageBubble isSender={isSender}>
        {variant === 'text' && (
          <>
            <SkeletonText
              isSender={isSender}
              variant="text"
              width={`${getRandomWidth()}%`}
              height={getRandomHeight()}
              animation="wave"
            />
            <SkeletonText
              isSender={isSender}
              variant="text"
              width={`${getRandomWidth() - 20}%`}
              height={16}
              animation="wave"
              sx={{ mt: 1 }}
            />
          </>
        )}
        {variant === 'media' && (
          <>
            <SkeletonText
              isSender={isSender}
              variant="rectangular"
              width="200px"
              height="120px"
              animation="wave"
              sx={{ borderRadius: '8px' }}
            />
            <SkeletonText
              isSender={isSender}
              variant="text"
              width="60%"
              height={16}
              animation="wave"
              sx={{ mt: 1 }}
            />
          </>
        )}
        <SkeletonTimestamp
          isSender={isSender}
          variant="text"
          width="40px"
          height={12}
          animation="wave"
        />
      </SkeletonMessageBubble>
      {isSender && (
        <SkeletonAvatar 
          isSender={isSender}
          variant="circular"
          animation="wave"
        />
      )}
    </SkeletonMessageContainer>
  );
}

export default MessageSkeleton;
